/**
 * Helper de redirection vers la page de login en respectant le basename React.
 *
 * En développement local : basename = '/gestioncaisse' → URL '/gestioncaisse/login'
 * En production : idem (https://sag-ecom.sag.ci/gestioncaisse/login)
 *
 * À utiliser à la place de `window.location.href = '/login'` qui bypasse le
 * BrowserRouter et ignore le basename, ce qui produit un 404 nginx en prod.
 *
 * Pour les composants React, préférer `navigate('/login')` de react-router-dom
 * qui applique automatiquement le basename.
 */

/**
 * Récupère le basename React de l'application (ex: '/gestioncaisse').
 * Exporté pour que d'autres helpers puissent construire des URLs absolues
 * cohérentes (ex: window.open en nouvel onglet) qui respectent le basename.
 */
import { loginPathForBuild } from './postLogout';

export function getBasename(): string {
  // Astuce : on lit le basename injecté en HTML par CRA via le tag <base>
  // ou on hardcode la valeur (cohérente avec App.tsx <BrowserRouter basename='/gestioncaisse'>).
  // En cas d'incohérence locale/prod, surcharger via REACT_APP_BASENAME.
  const fromEnv = process.env.REACT_APP_BASENAME;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  // Fallback : tag <base href="..."> injecté par CRA depuis package.json#homepage
  if (typeof document !== 'undefined') {
    const baseEl = document.querySelector('base');
    if (baseEl) {
      const href = baseEl.getAttribute('href') || '/';
      // <base href="/gestioncaisse/"> → '/gestioncaisse'
      try {
        const url = new URL(href, window.location.origin);
        return url.pathname.replace(/\/$/, '');
      } catch {
        return href.replace(/\/$/, '');
      }
    }
  }
  // Défaut SAGeCom
  return '/gestioncaisse';
}

/**
 * Liste des chemins publics qui ne doivent JAMAIS provoquer une redirection
 * automatique vers /login même si une requête API renvoie 401.
 * Ces pages doivent rester accessibles aux visiteurs non connectés.
 *
 * IMPORTANT — synchroniser cette liste avec `PUBLIC_PATHS` de `AppRoutes.tsx`.
 * Une route absente ici provoque un bug en prod : un 401 silencieux (token
 * expiré, requête publique sur endpoint non auth) redirige l'utilisateur
 * vers /login alors qu'il consulte une page publique.
 */
const PUBLIC_PATHS = [
  '/landing',
  '/login',
  '/sso/callback',
  '/souscrire',
  '/postuler',
  '/devis-validation',
  '/bien-public',
  '/contre-proposition',
  '/contrat-validation',
  '/espace-public',
  '/portail',
  '/api-zone',
  // Socle « Vitrine Patronat » — pages publiques servies à la racine.
  '/public/',
  '/change-password',
  '/unauthorized',
  '/mot-de-passe-oublie',
  '/reinitialiser-mot-de-passe',
  '/demo-vie-scolaire',
  '/espace-parent',
  '/paiement/retour',
  '/cinetpay/retour',
  '/cotation',
  // Portail Patronat / Association — accès agents + adhérents via slug
  // (LoginPatronatAgentsPage et PortailPatronatPage)
  '/p/agents',
  '/p/adherents',
  // AGEE — Accord public frais de dossier (token JWT dans URL, envoyé par email)
  '/public/agee',
];

/** Vérifie si le chemin courant correspond à une route publique. */
function isOnPublicPath(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return PUBLIC_PATHS.some(p => path.includes(p));
}

/**
 * Redirige le navigateur vers la page de login en construisant l'URL avec
 * le bon basename. Utilise `window.location.href` (full reload) — adapté
 * aux interceptors axios qui ne sont pas dans le scope React Router.
 *
 * Ne fait rien si l'utilisateur est déjà sur une page publique (landing,
 * portail, souscrire, etc.) — empêche les boucles de redirection lorsqu'un
 * token expiré dans localStorage déclenche un 401 sur une page publique.
 */
export function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  // Ne pas rediriger si on est déjà sur login ou sur une route publique
  if (isOnPublicPath()) return;
  const basename = getBasename();
  // Build CGE-GUI : redirige selon l'espace (adhérent → /connexion, sinon /acces-collaborateurs).
  window.location.href = `${basename}${loginPathForBuild()}`;
}

/**
 * Variante : retourne juste l'URL absolue (pour compatibilité ancienne signature).
 */
export function getLoginUrl(): string {
  return `${getBasename()}${loginPathForBuild()}`;
}
